/**
 * AirportSigns.js
 *
 * Airport wayfinding signs used as links after the party (/fly/thank-you, /fly/souvenir): a panel with a pictogram,
 * lettering and an arrow, like the overhead signs in a terminal. The panel is the airline's blue, or the signed-in
 * guest's own colour.
 */

import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import { contrast } from "./Passport";

export const PHOTOS_URL = "https://photos.app.goo.gl/pZoovyauTG1b8EPAA";

// The captain's note's blue: the signs' colour unless the signed-in guest has their own
export const AIRLINE_BLUE = "#007CA0";
const CREAM = "#FBF7EF";
const INK = "#1B2A41";

// Cream or ink lettering, whichever reads better on the panel
const letteringOn = (colour) => (contrast(CREAM, colour) >= contrast(INK, colour) ? CREAM : INK);
const SIGN_FONT = "'IBM Plex Sans Condensed', 'Arial Narrow', system-ui, sans-serif";

const ARROWS = { right: ArrowRight, left: ArrowLeft, out: ArrowUpRight };

/**
 * One sign
 * @param {Component} icon      -   A lucide icon, the pictogram
 * @param {String} title        -   e.g. 'Souvenirs'
 * @param {String} subtitle     -   e.g. 'Boarding pass & passport'
 * @param {String} arrow        -   'right' | 'left' | 'out' (opens another site)
 * @param {String} to / href    -   A page on the site, or another site
 * @param {String} colour       -   The panel's colour (default: the airline's blue)
 */
export const Sign = ({ icon: Icon, title, subtitle, arrow = "right", to, href, colour = AIRLINE_BLUE }) => {
    const Arrow = ARROWS[arrow];
    const lettering = letteringOn(colour);
    const left = arrow === "left";
    const inner = (
        <>
            {left && <Arrow size={30} strokeWidth={2.6} color={lettering} aria-hidden="true" className="shrink-0" />}
            <span className="grid place-items-center shrink-0 rounded-md" style={{ width: 52, height: 52, background: CREAM }}>
                <Icon size={30} strokeWidth={2.2} color={colour} aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col text-left">
                <span className="text-[22px] font-semibold leading-tight" style={{ color: lettering }}>{title}</span>
                {subtitle && <span className="text-sm font-medium leading-tight" style={{ color: lettering, opacity: 0.9 }}>{subtitle}</span>}
            </span>
            {!left && <Arrow size={30} strokeWidth={2.6} color={lettering} aria-hidden="true" className="shrink-0" />}
        </>
    );
    const className = "flex w-full min-h-[76px] items-center gap-4 rounded-xl px-4 py-3 transition hover:brightness-110 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4";
    const style = { background: colour, fontFamily: SIGN_FONT, boxShadow: "0 6px 18px rgba(27,42,65,0.22), inset 0 -3px 0 rgba(0,0,0,0.25)" };
    return href
        ? <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={style}>{inner}</a>
        : <Link to={to} className={className} style={style}>{inner}</Link>;
};

/**
 * Signs stacked like a sign gantry, with a small heading
 */
export const SignGantry = ({ heading = "THIS WAY, PLEASE", children }) => (
    <nav aria-label={heading} className="flex flex-col gap-3">
        <div className="text-center text-[11px] font-bold tracking-[0.24em]" style={{ color: "#5B6577" }}>{heading}</div>
        {children}
    </nav>
);
